<script setup lang="ts">
import { useSegmentationDialog } from '@/src/composables/useSegmentationDialog';

const segDialog = useSegmentationDialog();
</script>

<template>
  <v-dialog v-model="segDialog.showDialog.value" max-width="900" scrollable>
    <v-card>
      <v-card-title class="text-title-large">
        Segmentation Pre-Check
      </v-card-title>
      <v-card-subtitle>
        Review orientation classification and image count for each selected
        viewer.
      </v-card-subtitle>

      <v-card-text>
        <div class="mb-4">
          <div class="text-body-large mb-2">
            Global Model (SAX)
          </div>
          <v-select
            v-model="segDialog.selectedModel.value"
            :items="segDialog.modelSettingOptions"
            density="compact"
            hide-details
          />
        </div>
        <v-alert
          v-if="segDialog.hasUndefinedOrientation.value"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          At least one selected viewer has Undefined orientation. Segmentation
          cannot start until orientation is classified.
        </v-alert>
        <v-alert
          v-if="segDialog.hasDuplicateSeriesSlice.value"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          Multiple viewers are displaying the same series and slice index.
          Ensure each viewer is showing different image data.
        </v-alert>
        <v-alert
          v-if="segDialog.hasDuplicateViewerID.value"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          Duplicate viewer IDs were provided. Only unique viewers are shown.
        </v-alert>

        <div v-if="segDialog.hasSummaries.value" class="viewer-grid">
          <v-card
            v-for="summary in segDialog.uniqueViewerSummaries.value"
            :key="summary.viewerID"
            variant="outlined"
            class="viewer-card"
          >
            <v-card-title class="text-body-large">
              {{ summary.viewerID }} (Series {{ summary.seriesIdx + 1 }}, Slice
              {{ summary.sliceIdx + 1 }})
            </v-card-title>
            <v-card-text>
              <div class="viewer-row">
                <span class="viewer-label">Orientation</span>
                <v-select
                  :items="segDialog.availableOrientations"
                  :model-value="
                    segDialog.selectedOrientations.value[summary.viewerID]
                  "
                  hide-details
                  density="compact"
                  variant="outlined"
                  @update:model-value="
                    (val) =>
                      segDialog.handleOrientationUpdate(summary.viewerID, val)
                  "
                />
              </div>

              <!-- Scope selector — hidden when imageUIDs were pre-provided -->
              <div
                v-if="!segDialog.hasPreProvidedUIDs(summary.viewerID)"
                class="viewer-row"
              >
                <span class="viewer-label">Images</span>
                <v-select
                  v-model="segDialog.selectedImageScope.value[summary.viewerID]"
                  :items="['All in Slice', 'Breathhold']"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </div>

              <div class="viewer-row">
                <span class="viewer-label">Num Images</span>
                <span>{{ summary.imageCount }}</span>
              </div>

              <div
                v-if="
                  !segDialog.hasPreProvidedUIDs(summary.viewerID)
                    && segDialog.selectedImageScope.value[summary.viewerID]
                      === 'Breathhold'
                "
                class="viewer-row"
              >
                <span class="viewer-label">Breathhold Range</span>
                <span>{{
                  segDialog.getBreathholdRange(summary.viewerID)
                }}</span>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="
            segDialog.isStartingSegmentation.value
              || segDialog.isSegmentationActive.value
          "
          :disabled="
            segDialog.hasUndefinedOrientation.value
              || !segDialog.hasSummaries.value
              || segDialog.uniqueViewerSummaries.value.length === 0
          "
          @click="segDialog.startSegmentation"
        >
          Start Segmentation
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.viewer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
}

.viewer-card {
  border-radius: 10px;
}

.viewer-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.viewer-label {
  font-weight: 600;
}
</style>
